/**
 * UsageMeter — chat cost caps (daily / weekly / monthly) + storage.
 *
 * Jun 13, 2026 — scope reduced. The V1-style query-count KPI grid
 * (Quick / Standard / Expert × 0) and the feature checklist (Modalità
 * Expert / Ragionamento / Web search) were dropped per user direction
 * — those signals already live in the "Il valore di Ando questo mese"
 * insights card above (the query-mix bar inside ``Query totali``).
 * Repeating them here read as noise on a page that should focus on
 * cap headroom.
 *
 * What stays: the 4-up budget strip (daily / weekly / monthly chat
 * cost + storage), percent-only customer-facing display. Honours
 * ``feedback_customer_facing_no_euros_only_pct`` — never renders raw
 * EUR. Internal admin views can still consume the EUR fields off the
 * BE response.
 *
 * Tone routing mirrors the BE thresholds verbatim:
 *   - ``<70%``     → success (plenty of headroom)
 *   - ``70-89%``   → warning (in-band, no alarm)
 *   - ``≥90%``     → error + animate-pulse on the percent number
 *
 * Bug fixes applied in this rebuild:
 *   - i18n key leak (``app.billing.costCap.monthlyChat``) — proper
 *     Lingui ``msg\`\``` macros now, no ``t() || fallback`` hack.
 *   - double-colon limit hint (``Limite raggiunto: : 100%``) — uses
 *     ``·`` as separator so pre-punctuated catalog strings don't
 *     collide.
 */

import type {
  BillingSubscriptionResponse,
  BillingUsageResponse,
  DailyUsageResponse,
  WeeklyUsageResponse,
} from "@ando/core";
import { cn } from "@ando/ui";
import type * as React from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatPct(pct: number): string {
  // Italian locale → comma decimal separator. Clamp 0-100; show one
  // decimal under 10% so a tiny "0,3%" doesn't print as "0%" (which
  // tricks the eye into thinking the bar is at zero).
  const clamped = Math.max(0, Math.min(100, pct));
  const digits = clamped > 0 && clamped < 10 ? 1 : 0;
  return `${clamped.toLocaleString("it-IT", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}%`;
}

type Tone = "success" | "warning" | "error";

/** Pure helper exported for the test seam. */
export function usageTone(pct: number): Tone {
  if (pct >= 90) return "error";
  if (pct >= 70) return "warning";
  return "success";
}

function toneText(tone: Tone): string {
  switch (tone) {
    case "error":
      return "text-error";
    case "warning":
      return "text-warning";
    default:
      return "text-success";
  }
}

function toneBar(tone: Tone): string {
  switch (tone) {
    case "error":
      return "bg-error";
    case "warning":
      return "bg-warning";
    default:
      return "bg-success";
  }
}

// ─── BudgetMeter — single cost-cap card ───────────────────────────────────

interface BudgetMeterProps {
  readonly label: string;
  readonly pct: number;
  readonly hint?: string;
  readonly hintTone?: Tone;
  readonly ariaValueText?: string;
}

const BudgetMeter: React.FC<BudgetMeterProps> = ({
  label,
  pct,
  hint,
  hintTone,
  ariaValueText,
}) => {
  const tone = usageTone(pct);
  const safePct = Math.max(0, Math.min(100, pct));
  const isCritical = tone === "error";
  return (
    <div className="flex flex-col gap-2 rounded-md border border-hairline bg-card/40 p-3.5">
      <div className="flex items-baseline justify-between gap-2">
        <p className="truncate font-medium text-[12px] text-ink-2">{label}</p>
        <p
          className={cn(
            "font-display font-semibold text-[15px] tabular-nums",
            toneText(tone),
            isCritical && "animate-pulse",
          )}
        >
          {formatPct(safePct)}
        </p>
      </div>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(safePct)}
        aria-valuetext={ariaValueText}
        aria-label={`${label}, ${Math.round(safePct)}%`}
        className="h-1.5 overflow-hidden rounded-full bg-ink-1/[0.06]"
      >
        <div
          className={cn(
            "h-full rounded-full transition-[width] duration-200 motion-reduce:transition-none",
            toneBar(tone),
          )}
          style={{ width: `${safePct}%` }}
        />
      </div>
      {hint ? (
        <p className={cn("text-[11px]", toneText(hintTone ?? tone))}>{hint}</p>
      ) : null}
    </div>
  );
};
BudgetMeter.displayName = "UsageMeter.BudgetMeter";

// ─── Main component ──────────────────────────────────────────────────────

export interface UsageMeterProps {
  readonly subscription?: BillingSubscriptionResponse | null;
  readonly usage: BillingUsageResponse;
  readonly dailyUsage?: DailyUsageResponse;
  readonly weeklyUsage?: WeeklyUsageResponse;
  readonly className?: string;
}

export const UsageMeter: React.FC<UsageMeterProps> = ({
  subscription,
  usage,
  dailyUsage,
  weeklyUsage,
  className,
}) => {
  const { i18n } = useLingui();

  // Tier-aware monthly label — Free tier shows "Prova gratuita" so the
  // 100% cap doesn't read as catastrophic (free users hitting 100% just
  // means "trial is exhausted, upgrade").
  const tierIsFree = subscription?.tier?.toLowerCase() === "free";
  const monthlyLabel = tierIsFree
    ? i18n._(msg`Prova gratuita`)
    : i18n._(msg`Chat — mese corrente`);

  /** Contextual hint — only shown when the tone says we're approaching
   *  or at the limit, so quiet meters stay quiet (no "0% — all good"
   *  noise). Uses ``·`` separator (not ``:``) to avoid the V1
   *  double-colon bug when the i18n catalogue happens to ship a
   *  pre-punctuated string. */
  const budgetHint = (pct: number): { text: string; tone: Tone } | undefined => {
    if (pct >= 95) {
      return {
        text: `${i18n._(msg`Limite raggiunto`)} · ${Math.round(pct)}%`,
        tone: "error",
      };
    }
    if (pct >= 80) {
      return {
        text: `${i18n._(msg`In avvicinamento`)} · ${Math.round(pct)}%`,
        tone: "warning",
      };
    }
    return undefined;
  };

  const monthlyPct = dailyUsage?.monthly_usage_pct ?? usage.cost_pct ?? 0;
  const dailyPct = dailyUsage?.daily_usage_pct ?? 0;
  const weeklyPct = weeklyUsage?.weekly_usage_pct ?? 0;

  const storageLimitBytes = usage.storage_limit_bytes ?? 0;
  const storagePct = usage.storage_pct ?? 0;
  const storageUnlimited = storageLimitBytes === -1 || storageLimitBytes === 0;
  const storageSummary = storageUnlimited
    ? i18n._(msg`Illimitato`)
    : `${formatBytes(usage.storage_used_bytes)} / ${formatBytes(storageLimitBytes)}`;

  const monthlyHint = budgetHint(monthlyPct);
  const dailyHint = budgetHint(dailyPct);
  const weeklyHint = budgetHint(weeklyPct);

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {/* Budget strip — 4-up on desktop, 2-up on tablet, stacked on
          mobile. Each meter is a percent-only customer-facing surface
          (no raw EUR — see file docstring). */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {dailyUsage ? (
          <BudgetMeter
            label={i18n._(msg`Costo · giornaliero`)}
            pct={dailyPct}
            hint={dailyHint?.text}
            hintTone={dailyHint?.tone}
          />
        ) : null}
        {weeklyUsage ? (
          <BudgetMeter
            label={i18n._(msg`Costo · settimanale`)}
            pct={weeklyPct}
            hint={weeklyHint?.text}
            hintTone={weeklyHint?.tone}
          />
        ) : null}
        <BudgetMeter
          label={monthlyLabel}
          pct={monthlyPct}
          hint={monthlyHint?.text}
          hintTone={monthlyHint?.tone}
        />
        <BudgetMeter
          label={i18n._(msg`Storage`)}
          pct={storageUnlimited ? 0 : storagePct}
          hint={storageSummary}
          ariaValueText={
            storageUnlimited
              ? i18n._(msg`Storage illimitato`)
              : `${formatBytes(usage.storage_used_bytes)} usati di ${formatBytes(storageLimitBytes)}`
          }
        />
      </div>

    </div>
  );
};
UsageMeter.displayName = "UsageMeter";
